import {
  SBState,
  SBSelectRaw,
  getIdOrModelId,
  baseReducers,
} from '../helpers/ReducerHelper'
import {
  SBAPICreate,
  SBAPIDelete,
  SBAPIFetchPaginatedDispatch,
  SBAPIUpdate,
} from '../helpers/SBAPIHelper'
import { Template } from '../models/Template'
import { AppDispatch, RootState } from './store'
import { TableParams } from '../models/TableParams'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { TEMPLATE_URL } from '../helpers/urls'
import { templateSchema } from '../models/schema'

const initialState: SBState<Template> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: {
    pagination: {
      current: 1,
      pageSize: 10,
    },
  },
}

const slice = createSlice({
  name: 'template',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getTemplatesSuccess,
  setQuery: setTemplateQuery,
  reset: resetTemplateState,
  resetQueryAndIds: resetTemplateQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: Template }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectTemplates = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedTemplate = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectTemplateById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectTemplatesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedTemplate =
  (template: Template | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Template>(template)))

export const getTemplates = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<Template>(
    TEMPLATE_URL,
    params,
    [templateSchema],
    slice.actions
  )

export const createTemplate = (template: Template) =>
  SBAPICreate<Template>(template, TEMPLATE_URL, templateSchema, slice.actions)

export const updateTemplate = (template: Template) =>
  SBAPIUpdate<Template>(
    template,
    `${TEMPLATE_URL}/${template.id}`,
    slice.actions
  )

export const deleteTemplate = (template: Template) =>
  SBAPIDelete<Template>(
    template,
    `${TEMPLATE_URL}/${template.id}`,
    slice.actions
  )
